import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "./button";
import { Input } from "./input";
import { Label } from "./label";
import { authStyles } from "./auth-styles";

const MIN_PASSWORD_LENGTH = 8;

export interface SetPasswordScreenProps {
  /** Account the recovery session belongs to, shown under the heading. */
  email?: string;
  onSetPassword: (password: string) => Promise<void> | void;
  error?: React.ReactNode;
}

/**
 * Shared set/reset-password screen (admin + client portals). Rendered when a
 * Supabase invite/recovery link lands with a recovery session — same dark
 * auth-card styling as the login screen.
 */
export function SetPasswordScreen({ email, onSetPassword, error }: SetPasswordScreenProps) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [localError, setLocalError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLocalError("");

    if (password.length < MIN_PASSWORD_LENGTH) {
      setLocalError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setLocalError("Passwords don't match.");
      return;
    }

    setLoading(true);
    try {
      await onSetPassword(password);
    } finally {
      setLoading(false);
    }
  };

  const message = localError || error;

  return (
    <div className="login-backdrop">
      <style>{authStyles}</style>

      <div className="login-card">
        <h1 className="login-title">Choose your password</h1>
        <p className="login-subtitle">
          {email ? `You're signed in as ${email}. ` : ""}Set a password to use
          when signing in from now on.
        </p>

        <form onSubmit={handleSubmit} className="login-form">
          <div className="login-field">
            <Label htmlFor="new-password" className="login-label">New password</Label>
            <Input
              id="new-password"
              type="password"
              value={password}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPassword(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              placeholder="••••••••"
              autoComplete="new-password"
              className="login-input"
            />
          </div>
          <div className="login-field">
            <Label htmlFor="confirm-password" className="login-label">Confirm password</Label>
            <Input
              id="confirm-password"
              type="password"
              value={confirm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setConfirm(e.target.value)}
              required
              minLength={MIN_PASSWORD_LENGTH}
              placeholder="••••••••"
              autoComplete="new-password"
              className="login-input"
            />
          </div>
          <Button type="submit" disabled={loading} className="login-button">
            {loading ? <Loader2 className="login-spinner" /> : null}
            {loading ? "Saving..." : "Set password"}
          </Button>
          {message && <p className="login-error">{message}</p>}
        </form>
      </div>
    </div>
  );
}
